import { ArrowsClockwiseIcon, CaretDownIcon, CaretRightIcon, CubeIcon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import { ResourceTreeAction, ResourceTreeRow } from "../../ResourceExplorerTree.tsx";
import { containerTargetKey, type ContainerBinding } from "../../api/containers/model.ts";
import { useContainers } from "./react.tsx";
import { formatContainerBytes as formatBytes } from "./model.ts";

interface ContainerExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function ContainerExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange
}: ContainerExplorerProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [instance, setInstance] = useState("main");
  const view = useContainers(workspaceId, instance, refreshToken);
  const snapshot = view.catalog.value, bindings = snapshot?.bindings ?? [];
  const loading = view.catalog.loading, busy = view.busy, error = view.error;
  useEffect(() => { setExpanded(null); }, [workspaceId, refreshToken]);
  const control = (binding: ContainerBinding, action: "state" | "start" | "stop" | "destroy") => {
    if (!view.ready || !workspaceId || !snapshot || !instance.trim()) return;
    view.dispatch({ _tag: "Control", target: { workspaceId, revision: snapshot.revision, bindingName: binding.bindingName, instance }, selectionEpoch: view.catalog.selectionEpoch, action });
  };

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            {collapsed ? <CaretRightIcon size={10} /> : <CaretDownIcon size={10} />}
            Containers
          </button>
        )}
        <ResourceTreeAction label="Refresh Containers" onClick={() => view.dispatch({ _tag: "Refresh" })}>
          <ArrowsClockwiseIcon size={13} className={loading ? "animate-spin" : ""} />
        </ResourceTreeAction>
      </div>
      {!collapsed ? (
        <div role="tree" className="min-h-0 flex-1 overflow-auto py-1">
          {workspaceId && bindings.length ? (
            <>
              <ResourceTreeRow
                depth={0}
                label={connectionName || "Workerspace"}
                detail={`${bindings.length} binding${bindings.length === 1 ? "" : "s"}`}
                icon={<CubeIcon size={14} />}
              />
              {bindings.map((binding) => {
                const open = expanded === binding.bindingName;
                const target = { workspaceId: workspaceId ?? "", revision: snapshot?.revision ?? "", bindingName: binding.bindingName, instance };
                const observed = view.mutation.states.get(containerTargetKey(target));
                const state = observed?.selectionEpoch === view.catalog.selectionEpoch ? observed.state : undefined;
                return (
                  <div key={binding.bindingName}>
                    <ResourceTreeRow
                      depth={1}
                      label={binding.bindingName}
                      detail={`${binding.className}${binding.defaultPort ? ` · :${binding.defaultPort}` : ""}`}
                      icon={<CubeIcon size={14} />}
                      expandable
                      expanded={open}
                      onClick={() => setExpanded(open ? null : binding.bindingName)}
                      title={`${binding.image}\nrootless DIND · host network · ephemeral storage`}
                    />
                    {open ? (
                      <div className="mx-2 mb-2 ml-7 space-y-1 rounded border border-kumo-line bg-kumo-base/50 p-2 text-[10px] text-kumo-subtle">
                        <div className="break-all text-kumo-default">{binding.image}</div>
                        <div>
                          {binding.imageSource}
                          {binding.buildContext
                            ? ` · ${binding.buildContext.fileCount} files · ${formatBytes(binding.buildContext.bytes)}`
                            : ""}
                          {binding.sleepAfter !== undefined ? ` · sleeps ${binding.sleepAfter}` : ""}
                        </div>
                        <label className="block">
                          <span className="sr-only">Container instance id</span>
                          <input
                            value={instance}
                            onChange={(event) => setInstance(event.target.value)}
                            maxLength={256}
                            placeholder="instance id"
                            className="h-6 w-full rounded border border-kumo-line bg-kumo-base px-2 text-[11px] text-kumo-default outline-none focus:border-kumo-accent"
                          />
                        </label>
                        <div className="flex flex-wrap gap-1">
                          {(["state", "start", "stop", "destroy"] as const).map((action) => (
                            <button
                              key={action}
                              type="button"
                              disabled={busy || !instance.trim() || !view.ready}
                              aria-busy={!view.ready ? "true" : undefined}
                              onClick={() => void control(binding, action)}
                              className={`rounded border border-kumo-line px-2 py-0.5 capitalize text-kumo-default hover:bg-kumo-fill disabled:opacity-50 ${action === "destroy" ? "text-status-error" : ""}`}
                            >
                              {view.mutation.pending.some(value => value.selectionEpoch === view.catalog.selectionEpoch && containerTargetKey(value.target) === containerTargetKey(target) && value.action === action) ? "working…" : action}
                            </button>
                          ))}
                        </div>
                        {state ? (
                          <div role="status" className="text-kumo-default">
                            {instance}: {state.status}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </>
          ) : workspaceId ? (
            <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
              Add native Wrangler containers and a matching durable_objects binding, save, then refresh.
            </div>
          ) : (
            <p className="p-2 text-[11px] text-kumo-subtle">
              Open a workerspace to inspect Containers.
            </p>
          )}
          {error ? <p className="px-2 py-1 text-[11px] text-status-error">{error}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
